import type { ScanResult, ScanFieldKey } from "../../types/scan";
import { needsFieldVerification } from "./ocr";
import {
  showTreatmentNotification,
  showErrorNotification,
  SCAN_NOTIFICATION_DURATION_MS,
} from "../Notification";

const FIELD_LABELS: Record<ScanFieldKey, string> = {
  lastname: "Nom",
  firstname: "Prénom",
  company: "Entreprise",
  btp_number: "N° carte",
};

export type QrReadStatus = "read" | "not_read";

export function getQrReadStatus(result: ScanResult): QrReadStatus {
  return result.qrResult?.url ? "read" : "not_read";
}

export function getQrValiditySummary(result: ScanResult): {
  status: "valid" | "invalid" | "pending" | "unknown";
  label: string;
  detail?: string;
} {
  if (getQrReadStatus(result) === "not_read") {
    return { status: "unknown", label: "Validité non vérifiable (QR absent)" };
  }
  const q = result.qrResult!;
  if (q.status === "valid") {
    return { status: "valid", label: "Carte valide" };
  }
  if (q.status === "invalid") {
    return {
      status: "invalid",
      label: "Carte invalide",
      detail: q.message,
    };
  }
  return {
    status: "pending",
    label: "Validité en attente",
    detail: q.message ?? "Vérification à la synchronisation",
  };
}

export function getLowConfidenceFields(result: ScanResult): ScanFieldKey[] {
  const conf = result.fieldConfidence ?? {};
  const keys: ScanFieldKey[] = ["lastname", "firstname", "company", "btp_number"];
  return keys.filter((key) => {
    const value =
      key === "btp_number" ? result.btp_number : (result[key] as string | undefined);
    return needsFieldVerification(conf[key], Boolean(value?.trim()));
  });
}

export function buildScanSummaryMessage(result: ScanResult): string {
  const parts: string[] = [];

  if (getQrReadStatus(result) === "read") {
    const validity = getQrValiditySummary(result);
    parts.push(`QR code détecté. ${validity.label}.`);
    if (validity.detail && validity.status !== "valid") {
      parts.push(validity.detail);
    }
  } else {
    parts.push("QR code non détecté. La validité de la carte n'a pas pu être vérifiée.");
  }

  const verifyFields = getLowConfidenceFields(result).filter(
    (key) => key !== "btp_number" || !result.qrResult?.btp_number
  );
  if (verifyFields.length > 0) {
    const labels = verifyFields.map((key) => FIELD_LABELS[key]).join(", ");
    parts.push(`Vérifiez : ${labels}.`);
  }

  if (result.ocrTimedOut) {
    parts.push("Lecture automatique partielle — complétez les champs manquants.");
  }

  return parts.join(" ");
}

export function showScanFeedbackNotification(result: ScanResult): void {
  const message = buildScanSummaryMessage(result);
  if (!message.trim()) return;

  const validity = getQrValiditySummary(result);
  if (validity.status === "invalid") {
    showErrorNotification(message, SCAN_NOTIFICATION_DURATION_MS);
    return;
  }
  showTreatmentNotification(message, SCAN_NOTIFICATION_DURATION_MS);
}
